import { useMemo, useState } from 'react';
import { Calendar, Check } from 'lucide-react';
import { useRenewMembership } from '../queries';

type RenewMembershipModalProps = {
  open: boolean;
  membershipUuid?: string;
  currentExpiresAt?: string;
  onClose: () => void;
};

const RenewMembershipModal = ({ open, membershipUuid, currentExpiresAt, onClose }: RenewMembershipModalProps) => {
  const renewMutation = useRenewMembership();
  const [overrideDays, setOverrideDays] = useState('');
  const [error, setError] = useState('');

  const previewExpiry = useMemo(() => {
    const base = currentExpiresAt ? new Date(currentExpiresAt) : new Date();
    if (Number.isNaN(base.getTime())) return '—';
    const days = Number(overrideDays || 0);
    if (!days || days <= 0) return 'System default';
    const projected = new Date(base.getTime() + days * 24 * 60 * 60 * 1000);
    return projected.toLocaleDateString('en-IN', { dateStyle: 'medium' });
  }, [currentExpiresAt, overrideDays]);

  if (!open) return null;

  const handleRenew = async () => {
    if (!membershipUuid) return;
    setError('');
    try {
      await renewMutation.mutateAsync({
        membershipUuid,
        validityDaysOverride: overrideDays ? Number(overrideDays) : undefined
      });
      onClose();
    } catch (_error) {
      setError('Unable to renew membership right now.');
    }
  };

  return (
    <div className="membership-modal-overlay" onClick={onClose}>
      <div className="appointments-v2-card membership-modal-card" onClick={(event) => event.stopPropagation()}>
        <div className="appointments-v2-card-header"><h3>Renew membership</h3></div>
        <div className="appointments-v2-card-body membership-drawer-panel__form">
          <div className="membership-modal-banner is-success">
            <span className="membership-modal-banner__icon" aria-hidden="true"><Calendar size={16} /></span>
            <span>Current expiry: {currentExpiresAt ? new Date(currentExpiresAt).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—'}</span>
          </div>
          <label className="field">
            <span className="label">Validity override days (optional)</span>
            <input className="input" type="number" min={1} value={overrideDays} onChange={(e) => setOverrideDays(e.target.value)} />
          </label>
          <div className="membership-modal-banner is-success">
            <span className="membership-modal-banner__icon" aria-hidden="true"><Check size={16} /></span>
            <span>New expiry: <strong>{previewExpiry}</strong></span>
          </div>
          {error ? <p className="appointments-v2-empty-copy">{error}</p> : null}
          <div className="membership-drawer-panel__actions">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="button" className="btn primary" disabled={!membershipUuid || renewMutation.isPending} onClick={handleRenew}>
              {renewMutation.isPending ? 'Renewing...' : 'Renew'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenewMembershipModal;
